// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/growth-score/growth-score-model.ts (ADAPT)
// ADAPTED (plan P4-1, Step 4.2): TP's model BUILDS the assessment (the Match, the cohort, `buildAssessment`, the weights)
// from sample fixtures. Ours is built by the BFF, so `buildCohort`, `matchOf`, `matchParts`, `buildAssessment`,
// `handoffFromOnboarding` and `splitPicks` are dropped. What stays is what a page needs to PRESENT a number: the rank on
// a strip, the ad metric labels, the worst-check order and headline, the tones, and the copy context.
/**
 * Growth Score model, presentational helpers only: how a score reads (tone, headline, worst first), the ranks a
 * dot strip shows, and the context the check copy is written against. Pure functions; the pages only render them.
 */
import { SCORE_REFERENCE } from '@/contracts/onboarding-session'
import type { CopyContext } from './growth-score-content'
import type { AdMetricKey, BrandProfileInput, Check } from './growth-score.types'

/* ─── Rank against the cohort ───────────────────────────────────────────── */

export function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b)
  const m = s.length >> 1
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2
}

/** What a dot strip says about the brand's value among the cohort's. */
export interface MetricStats {
  /** The cohort's values. */
  values: number[]
  /** Brands ranked, the brand included. */
  n: number
  rank: number
  median: number
}

/** The brand's rank among the cohort values (higher is better), as TP's `metricStats` counted it; the median is the BFF's. */
export function stripStats(strip: { you: number; median: number; values: number[] }): MetricStats {
  const better = strip.values.filter((v) => v > strip.you).length
  return { values: strip.values, n: strip.values.length + 1, rank: better + 1, median: strip.median }
}

/** The five ad metrics, as the Ads "How we measured it" strips show them. */
export const AD_METRICS: { key: AdMetricKey; label: string; sub: string; unit: string }[] = [
  { key: 'lpw', label: 'Launches per week', sub: 'average per week', unit: '' },
  { key: 'win', label: 'Winners found', sub: 'scaling or long-lived ads', unit: '' },
  { key: 'kept7', label: 'Still live after 7 days', sub: 'share of new ads', unit: '%' },
  { key: 'topdl', label: 'Age of top ads', sub: 'median days live', unit: 'd' },
  { key: 'psh', label: 'Partnership ads', sub: 'share of top ads', unit: '%' },
]

/* ─── Checks, headline and tones ────────────────────────────────────────── */

/** A dimension's checks as opportunities: worst first, still-measuring and signals last. */
export const checksWorstFirst = <T extends Pick<Check, 'score'>>(checks: T[]): T[] =>
  [...checks].sort((a, b) => (a.score ?? 999) - (b.score ?? 999))

/** The weakest check by name only: a line about what the ads do (TP's "plenty of ads, too few last") would be a claim we cannot check. */
export function headline(worst: Pick<Check, 'name'>): string {
  return `Your weakest point is ${worst.name.toLowerCase()}.`
}

export type ScoreTone = 'bad' | 'warn' | 'neutral'
/** Pills: under half the reference is bad, under the reference (the cohort median) a warning; at or above it, neutral. */
const BAD_BELOW = SCORE_REFERENCE / 2
export const scoreTone = (v: number): ScoreTone => (v < BAD_BELOW ? 'bad' : v < SCORE_REFERENCE ? 'warn' : 'neutral')
/** How far from the reference a Search & AI bar reads as bad (below) or good (above); in between it is a warning. */
const BAR_BAND = 5
export const barTone = (v: number): 'bad' | 'warn' | 'good' =>
  v < SCORE_REFERENCE - BAR_BAND ? 'bad' : v > SCORE_REFERENCE + BAR_BAND ? 'good' : 'warn'

/* ─── Answers and the copy's context ────────────────────────────────────── */

export interface AnswerFacts {
  upsell: 'yes' | 'no' | 'unsure'
  subscribes: boolean
}

export function answerFacts(profile: BrandProfileInput): AnswerFacts {
  return {
    upsell: profile.hasPostPurchaseUpsell === true ? 'yes' : profile.hasPostPurchaseUpsell === false ? 'no' : 'unsure',
    subscribes: profile.sellsSubscriptions === true,
  }
}

/**
 * The context the check copy is written against. `strip` is the brand's value and the cohort median of an ad metric, read
 * from the BFF's strips (undefined when it sent none: the copy then quotes no figure); the brand is the name our own data gave
 * (never a sample).
 */
export const copyContext = (brand: string, facts: AnswerFacts, strip: CopyContext['strip']): CopyContext => ({
  brand,
  strip,
  ...facts,
})
